/**
 * The WebpageMetaData is used to set the SEO meta data for a page requeston the client.  This contains things such as meta robots tags, canonical links, and other meta attrbitues that are passed from the components configuration
 *
 * @module views/components/WebpageMetaData
 * @memberof -Common
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';

import PropTypes from 'prop-types';

/**
 * Represents a WebpageMetaData component
 *
 * @method
 * @param {WebpageMetaDataProps} props - React properties passed from composition
 * @returns WebpageMetaData
 */
export const WebpageMetaData = React.forwardRef( ( props, _ ) =>{

  const {
    metaTitle,
    metaDescription,
    canonical,
    robots,
    openGraphTitle,
    openGraphDescription,
    openGraphImage,
    openGraphUrl,
    openGraphVideo,
    seoStructuredData
  } = props;

  return (
    <>
      <Helmet>
        { metaTitle &&
        <title>
          { metaTitle }
        </title>
        }
        { metaDescription &&
        <meta
          name='description'
          content={ metaDescription }
        />
        }
        { canonical &&
        <link
          rel='canonical'
          href={ canonical }
        />
        }
        { robots &&
        <meta
          name='robots'
          content={ robots }
        />
        }
        { openGraphTitle &&
        <meta
          property='og:title'
          content={ openGraphTitle }
        />
        }
        { openGraphDescription &&
        <meta
          property='og:description'
          content={ openGraphDescription }
        />
        }
        { openGraphImage &&
        <meta
          property='og:image'
          content={ openGraphImage }
        />
        }
        { openGraphUrl &&
        <meta
          property='og:url'
          content={ openGraphUrl }
        />
        }
        { openGraphVideo &&
        <meta
          property='og:video'
          content={ openGraphVideo }
        />
        }
      </Helmet>

      { /*
      MFBOLT-2278: JSON-LD is rendered outside of <Helmet> because react-helmet-async (v2)
      strips <script> tags containing JSON-LD during Server-Side Rendering (SSR).
      Placing it here ensures React's native renderToString includes it in the HTML body
      so SEO bots can properly read it in the View Page Source.
    */ }
      { seoStructuredData &&
      <script type='application/ld+json' dangerouslySetInnerHTML={ { __html: JSON.stringify( seoStructuredData ) } } />
      }
    </>
  );
} );

/**
 * Property type definitions
 * @typedef WebpageMetaDataProps
 * @type {object}
 */
export const propTypes = {
  metaTitle: PropTypes.string,
  metaDescription: PropTypes.string,
  canonical: PropTypes.string,
  robots: PropTypes.string,
  openGraphTitle: PropTypes.string,
  openGraphDescription: PropTypes.string,
  openGraphImage: PropTypes.string,
  openGraphUrl: PropTypes.string,
  openGraphVideo: PropTypes.string,
  seoStructuredData: PropTypes.object
};

/**
 * Default values for passed properties
 * @type object
 */
export const defaultProps =  {
};

WebpageMetaData.propTypes = propTypes;
WebpageMetaData.defaultProps = defaultProps;
WebpageMetaData.displayName = 'WebpageMetaData';

export default WebpageMetaData;
